export interface User {
  id: string
  name: string
  email: string
  avatar?: string
  role: 'member' | 'moderator' | 'admin'
  joinDate: Date
  location?: string
  postCount: number
  reputation: number
}

export interface GroupCategory {
  id: string
  name: string
  description: string
  icon: string
  color: string
  groupCount: number
}

export interface Group {
  id: string
  name: string
  description: string
  category: string
  subcategory?: string
  icon: string
  coverImage?: string
  createdBy: User
  createdAt: Date
  updatedAt: Date
  memberCount: number
  postCount: number
  isPublic: boolean
  isFeatured: boolean
  rules?: string[]
  tags: string[]
  recentActivity: Date
  moderators: User[]
}

export interface Post {
  id: string
  groupId: string
  title: string
  content: string
  author: User
  createdAt: Date
  updatedAt: Date
  upvotes: number
  downvotes: number
  commentCount: number
  saves?: number
  isPinned: boolean
  isLocked: boolean
  tags: string[]
  attachments?: string[]
  views: number
  lastActivity: Date
}

export interface Comment {
  id: string
  postId: string
  content: string
  author: User
  createdAt: Date
  updatedAt: Date
  upvotes: number
  downvotes: number
  parentCommentId?: string // For nested comments
  isEdited: boolean
}

export interface GroupFilters {
  category?: string
  subcategory?: string
  search?: string
  isPublic?: boolean
  sortBy?: 'recent' | 'popular' | 'active'
  ids?: string[]
}

export interface PostFilters {
  groupId?: string
  authorId?: string
  search?: string
  sortBy?: 'recent' | 'popular' | 'top'
  isPinned?: boolean
}

// Default group categories for Brazilian community in US
export const GROUP_CATEGORIES: GroupCategory[] = [
  {
    id: 'housing',
    name: 'Moradia & Imóveis',
    description: 'Discussões sobre aluguel, compra, venda e dicas de imóveis',
    icon: '🏠',
    color: 'text-emerald-400',
    groupCount: 12,
  },
  {
    id: 'cars',
    name: 'Carros & Transporte',
    description: 'Compra, venda, manutenção e dicas sobre veículos',
    icon: '🚗',
    color: 'text-blue-400',
    groupCount: 8,
  },
  {
    id: 'jobs',
    name: 'Empregos & Carreira',
    description: 'Oportunidades de trabalho, currículos e networking',
    icon: '💼',
    color: 'text-amber-400',
    groupCount: 15,
  },
  {
    id: 'documents',
    name: 'Documentação & Vistos',
    description: 'Tirar dúvidas sobre vistos, green card e documentação',
    icon: '📋',
    color: 'text-purple-400',
    groupCount: 6,
  },
  {
    id: 'education',
    name: 'Educação & Cursos',
    description: 'Escolas, universidades, cursos e aprendizado',
    icon: '🎓',
    color: 'text-red-400',
    groupCount: 7,
  },
  {
    id: 'services',
    name: 'Serviços & Profissionais',
    description: 'Recomendações e busca por serviços especializados',
    icon: '🔧',
    color: 'text-green-400',
    groupCount: 10,
  },
  {
    id: 'community',
    name: 'Comunidade & Eventos',
    description: 'Eventos, encontros e atividades da comunidade brasileira',
    icon: '👥',
    color: 'text-pink-400',
    groupCount: 9,
  },
  {
    id: 'questions',
    name: 'Dúvidas & Ajuda',
    description: 'Tire suas dúvidas sobre a vida nos EUA',
    icon: '❓',
    color: 'text-cyan-400',
    groupCount: 11,
  },
  {
    id: 'politica',
    name: 'Política & Governo',
    description: 'Discussões sobre política brasileira e americana, eleições, leis',
    icon: '🏛️',
    color: 'text-blue-500',
    groupCount: 5,
  },
  {
    id: 'imigracao',
    name: 'Imigração & Vistos',
    description: 'Tire dúvidas sobre vistos, green card, cidadania e processos de imigração',
    icon: '🛂',
    color: 'text-green-500',
    groupCount: 8,
  },
  {
    id: 'saude',
    name: 'Saúde & Bem-estar',
    description: 'Sistema de saúde nos EUA, planos de saúde, dicas de bem-estar',
    icon: '🏥',
    color: 'text-red-500',
    groupCount: 6,
  },
  {
    id: 'cultura',
    name: 'Cultura & Entretenimento',
    description: 'Eventos culturais, música, cinema, tradições brasileiras e americanas',
    icon: '🎭',
    color: 'text-purple-500',
    groupCount: 7,
  },
  {
    id: 'tecnologia',
    name: 'Tecnologia & Inovação',
    description: 'Discussões sobre tecnologia, startups, carreira em TI, ferramentas digitais',
    icon: '💻',
    color: 'text-cyan-500',
    groupCount: 4,
  },
  {
    id: 'negocios',
    name: 'Negócios & Finanças',
    description: 'Empreendedorismo, investimentos, finanças pessoais, contabilidade',
    icon: '📈',
    color: 'text-amber-500',
    groupCount: 5,
  },
]

// Sample groups data
export const SAMPLE_GROUPS: Group[] = [
  {
    id: '1',
    name: 'Brasileiros em Orlando',
    description: 'Grupo para brasileiros morando ou visitando Orlando. Dicas, eventos e networking.',
    category: 'community',
    icon: '👥',
    coverImage: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800',
    createdBy: {
      id: 'user1',
      name: 'João Silva',
      email: 'joao@example.com',
      role: 'admin',
      joinDate: new Date('2023-01-15'),
      postCount: 45,
      reputation: 120,
    },
    createdAt: new Date('2023-03-10'),
    updatedAt: new Date('2026-04-02'),
    memberCount: 342,
    postCount: 156,
    isPublic: true,
    isFeatured: true,
    rules: [
      'Respeite todos os membros',
      'Não faça spam',
      'Mantenha as discussões relevantes',
    ],
    tags: ['orlando', 'florida', 'brasileiros', 'comunidade'],
    recentActivity: new Date('2026-04-02T10:30:00'),
    moderators: [
      {
        id: 'user2',
        name: 'Maria Souza',
        email: 'maria@example.com',
        role: 'moderator',
        joinDate: new Date('2023-02-20'),
        postCount: 28,
        reputation: 85,
      },
    ],
  },
  {
    id: '2',
    name: 'Imóveis em Miami',
    description: 'Compartilhe oportunidades de imóveis em Miami e região.',
    category: 'housing',
    icon: '🏠',
    coverImage: 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=800',
    createdBy: {
      id: 'user3',
      name: 'Carlos Mendes',
      email: 'carlos@example.com',
      role: 'admin',
      joinDate: new Date('2023-04-05'),
      postCount: 32,
      reputation: 95,
    },
    createdAt: new Date('2023-05-15'),
    updatedAt: new Date('2026-04-01'),
    memberCount: 189,
    postCount: 78,
    isPublic: true,
    isFeatured: true,
    rules: [
      'Apenas discussões sobre imóveis',
      'Compartilhe preços reais',
      'Não faça propaganda enganosa',
    ],
    tags: ['miami', 'imoveis', 'florida', 'aluguel', 'compra'],
    recentActivity: new Date('2026-04-01T14:45:00'),
    moderators: [],
  },
  {
    id: '3',
    name: 'Carros usados - EUA',
    description: 'Compre, venda e discuta sobre carros usados nos Estados Unidos.',
    category: 'cars',
    icon: '🚗',
    coverImage: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800',
    createdBy: {
      id: 'user4',
      name: 'Ana Costa',
      email: 'ana@example.com',
      role: 'admin',
      joinDate: new Date('2023-03-22'),
      postCount: 67,
      reputation: 150,
    },
    createdAt: new Date('2023-06-20'),
    updatedAt: new Date('2026-03-31'),
    memberCount: 256,
    postCount: 124,
    isPublic: true,
    isFeatured: false,
    rules: [
      'Informe preço real do veículo',
      'Compartilhe fotos originais',
      'Seja honesto sobre o estado do carro',
    ],
    tags: ['carros', 'usados', 'compra', 'venda', 'manutencao'],
    recentActivity: new Date('2026-03-31T09:15:00'),
    moderators: [
      {
        id: 'user5',
        name: 'Lucas Santos',
        email: 'lucas@example.com',
        role: 'moderator',
        joinDate: new Date('2023-07-10'),
        postCount: 41,
        reputation: 110,
      },
    ],
  },
]